// WhatsAppCalendar.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Stack,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  CalendarViewWeek,
  Add as AddIcon,
} from "@mui/icons-material";
import { format, startOfWeek, addDays, isSameDay } from "date-fns";
import { campaignApi } from "../services/campaignApi";

const BORDER = "#E0E0E0";

// ✅ IMPORTANT: normalize backend datetime to an unambiguous UTC ISO string
// If backend sends "2026-02-07 11:30:00" or "2026-02-07T11:30:00" (missing Z),
// we force it to "2026-02-07T11:30:00Z".
const normalizeUtcIso = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();

  // If it already has timezone info, return it
  if (/[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) return s;

  // Replace space with T, but DO NOT append Z
  if (s.includes(" ")) return s.replace(" ", "T");

  return s;
};
// ✅ Key for grouping campaigns under the correct day IN the campaign timezone
const dayKeyInTz = (rawIso, tz) => {
  const iso = normalizeUtcIso(rawIso);
  if (!iso) return null;

  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;

  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz || "Africa/Harare",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(dt);

    const get = (t) => parts.find((p) => p.type === t)?.value || "";
    const yyyy = get("year");
    const mm = get("month");
    const dd = get("day");
    if (!yyyy || !mm || !dd) return null;
    return `${yyyy}-${mm}-${dd}`; // YYYY-MM-DD
  } catch {
    // fallback: browser local (not ideal, but prevents crash)
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
};

function safeApiBase() {
  const base = (process.env.REACT_APP_API_URL || "").trim();
  return base.replace(/\/$/, "");
}

async function fetchCalendarFallback(startStr, endStr) {
  const base = safeApiBase();
  const url = `${base}/api/campaigns/calendar?start=${encodeURIComponent(
    startStr
  )}&end=${encodeURIComponent(endStr)}`;

  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    data = text;
  }

  if (!res.ok) {
    const msg =
      (data && data.message) ||
      (typeof data === "string" ? data : "") ||
      `Calendar request failed (${res.status})`;
    throw new Error(msg);
  }

  return data;
}

export default function WhatsAppCalendar({
  showHeader = false,
  onPlanNewBroadcast,
  onSelectCampaign, // when user clicks a calendar card
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekData, setWeekData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const startDate = useMemo(
    () => startOfWeek(currentDate, { weekStartsOn: 1 }),
    [currentDate]
  );

  // ✅ used for “disable plan new broadcast when week is fully in the past”
  const isWeekInPast = useMemo(() => {
    const endOfWeek = addDays(startDate, 6);
    const endOfWeekEnd = new Date(endOfWeek);
    endOfWeekEnd.setHours(23, 59, 59, 999);
    return endOfWeekEnd < new Date();
  }, [startDate]);

  const getColors = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "active":
      case "sending":
        return { bg: "#E8F5E9", border: "#4CAF50", chip: "success" };
      case "scheduled":
        return { bg: "#FFF3E0", border: "#FF9800", chip: "warning" };
      case "completed":
        return { bg: "#E3F2FD", border: "#2196F3", chip: "info" };
      case "failed":
        return { bg: "#FFEBEE", border: "#E53935", chip: "error" };
      case "paused":
        return { bg: "#F5F5F5", border: "#9E9E9E", chip: "default" };
      default:
        return { bg: "#FFFFFF", border: "#E0E0E0", chip: "default" };
    }
  };

  // ✅ TIME LABEL: normalize + format in campaign timezone
  const timeLabel = (rawIso, tz) => {
    if (!rawIso) return "—";

    const iso = normalizeUtcIso(rawIso);
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return "—";

    try {
      return new Intl.DateTimeFormat([], {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: tz || "Africa/Harare",
      }).format(dt);
    } catch {
      return dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
  };

  const normalizeCalendarPayload = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    return [];
  };

  // ✅ LOCK BY STATUS ONLY (paused is editable)
  const isCampaignLocked = (camp) => {
    const status = String(camp?.status || "").toLowerCase();
    // allow edit on scheduled + paused
    // lock only when running or finished
    return ["active", "sending", "completed", "failed", "cancelled"].includes(status);
  };

  const loadCalendarData = async () => {
    setLoading(true);
    setErr("");

    try {
      const startStr = format(startDate, "yyyy-MM-dd");
      const endStr = format(addDays(startDate, 6), "yyyy-MM-dd");

      let payload;

      const apiFn =
        campaignApi?.getCalendar ||
        campaignApi?.calendar ||
        campaignApi?.get_campaign_calendar ||
        null;

      if (typeof apiFn === "function") {
        const res = await apiFn(startStr, endStr);
        payload = res?.data ?? res;
      } else {
        payload = await fetchCalendarFallback(startStr, endStr);
      }

      const safe = normalizeCalendarPayload(payload);

      const days = Array.from({ length: 7 }).map((_, index) => {
        const dayDate = addDays(startDate, index);
        const dayStr = format(dayDate, "yyyy-MM-dd");

        // ✅ FIX: group by day in the campaign timezone (NOT startsWith on raw string)
        const dayCampaigns = safe
          .filter((c) => dayKeyInTz(c?.date, c?.timezone) === dayStr)
          .sort((a, b) => {
            const ad = new Date(normalizeUtcIso(a?.date));
            const bd = new Date(normalizeUtcIso(b?.date));
            return (ad?.getTime?.() || 0) - (bd?.getTime?.() || 0);
          });

        // ✅ day is past if end-of-day already passed
        const endOfDay = new Date(dayDate);
        endOfDay.setHours(23, 59, 59, 999);
        const isPastDay = endOfDay < new Date();

        return {
          dateObj: dayDate,
          short: format(dayDate, "EEE"),
          isToday: isSameDay(dayDate, new Date()),
          isPastDay,
          campaigns: dayCampaigns,
        };
      });

      setWeekData(days);
    } catch (e) {
      console.error("Failed to load calendar", e);
      setWeekData([]);
      setErr(e?.response?.data?.message || e?.message || "Failed to load calendar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalendarData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  const nextWeek = () => setCurrentDate((d) => addDays(d, 7));
  const prevWeek = () => setCurrentDate((d) => addDays(d, -7));

  // ✅ If week is in the past, disable plan new broadcast
  const canPlanInThisWeek = !isWeekInPast;

  return (
    <Box sx={{ width: "100%" }}>
      {showHeader && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={900}>
              WhatsApp Broadcast Weekly Plan ({format(startDate, "MMM d")} -{" "}
              {format(addDays(startDate, 6), "MMM d, yyyy")})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click a scheduled/paused card to edit or delete.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip
              title={
                canPlanInThisWeek
                  ? "Create a new broadcast"
                  : "This week is in the past. Move to a future week to plan."
              }
            >
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => onPlanNewBroadcast?.()}
                  disabled={!canPlanInThisWeek}
                  sx={{ textTransform: "none", boxShadow: "none", borderRadius: 999 }}
                >
                  Plan New Broadcast
                </Button>
              </span>
            </Tooltip>

            <Button
              size="small"
              variant="outlined"
              startIcon={<CalendarViewWeek />}
              onClick={loadCalendarData}
              sx={{ textTransform: "none", borderRadius: 999 }}
            >
              Refresh
            </Button>

            <IconButton size="small" onClick={prevWeek}>
              <ChevronLeft />
            </IconButton>
            <IconButton size="small" onClick={nextWeek}>
              <ChevronRight />
            </IconButton>
          </Stack>
        </Box>
      )}

      {err && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="error">
            {err}
          </Typography>
        </Box>
      )}

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            p: 5,
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
            bgcolor: "white",
            minHeight: 240,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(7, 1fr)" },
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
            overflow: "hidden",
            width: "100%",
            bgcolor: "white",
            minHeight: 520,
          }}
        >
          {weekData.map((day, index) => (
            <Box
              key={index}
              sx={{
                minHeight: 520,
                borderRight: { md: index < 6 ? `1px solid ${BORDER}` : "none" },
                borderBottom: { xs: `1px solid ${BORDER}`, md: "none" },
                bgcolor: day.isToday ? "#FFF9C4" : day.isPastDay ? "#FAFAFA" : "#FFFFFF",
                opacity: day.isPastDay ? 0.85 : 1,
              }}
            >
              <Box
                sx={{
                  p: 1.25,
                  bgcolor: day.isToday ? "#FFFDE7" : "#F5F5F5",
                  borderBottom: `1px solid ${BORDER}`,
                  textAlign: "center",
                }}
              >
                <Typography variant="subtitle2" fontWeight={900} color="text.secondary">
                  {day.short}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {format(day.dateObj, "MMM d")}
                </Typography>
              </Box>

              <Box sx={{ p: 1 }}>
                {day.campaigns?.length ? (
                  day.campaigns.map((camp) => {
                    const colors = getColors(camp?.status);
                    const locked = isCampaignLocked(camp);

                    return (
                      <Tooltip
                        key={camp?.id ?? `${index}-${camp?.title}`}
                        title={locked ? "Locked (running/finished)" : "Click to edit"}
                        placement="top"
                      >
                        <Paper
                          elevation={0}
                          onClick={() => {
                            if (locked) return;
                            onSelectCampaign?.(camp);
                          }}
                          sx={{
                            p: 1.25,
                            mb: 1.25,
                            bgcolor: colors.bg,
                            border: "1px solid",
                            borderColor: colors.border,
                            borderRadius: 2,
                            cursor: locked ? "not-allowed" : "pointer",
                            opacity: locked ? 0.6 : 1,
                            pointerEvents: "auto",
                            "&:hover": locked ? {} : { boxShadow: 2 },
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" color="text.secondary">
                              {camp?.date ? timeLabel(camp.date, camp.timezone) : "—"}
                            </Typography>

                            <Stack direction="row" spacing={0.75} alignItems="center">
                              {locked && (
                                <Chip
                                  size="small"
                                  label="Locked"
                                  variant="outlined"
                                  sx={{ fontWeight: 900 }}
                                />
                              )}
                              <Chip
                                size="small"
                                label={String(camp?.status || "unknown")}
                                color={colors.chip}
                                sx={{ textTransform: "capitalize", fontWeight: 800 }}
                              />
                            </Stack>
                          </Stack>

                          <Typography variant="subtitle2" fontWeight={900} sx={{ mt: 0.75 }}>
                            {camp?.title ?? "Untitled Campaign"}
                          </Typography>

                          {camp?.target && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              <strong>Target:</strong> {camp.target}
                            </Typography>
                          )}

                          {camp?.messages_per_hour != null && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              <strong>Speed:</strong> {camp.messages_per_hour}/hr
                            </Typography>
                          )}

                          {camp?.cooldown_hours != null && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              <strong>Cooldown:</strong> {camp.cooldown_hours}h
                            </Typography>
                          )}
                        </Paper>
                      </Tooltip>
                    );
                  })
                ) : (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ display: "block", textAlign: "center", mt: 6 }}
                  >
                    No Broadcasts
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
